import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { useT } from '@/lib/i18n';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Sparkles, User, Mail, Lock, Gift } from 'lucide-react';

const Register = () => {
  const { register } = useAuth();
  const { t, lang } = useT();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const refParam = params.get('ref');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [referral, setReferral] = useState(() => (refParam ? refParam.toUpperCase() : ''));
  const [showReferral, setShowReferral] = useState(() => Boolean(refParam));
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await register(email, password, name, referral.trim() || undefined);
      const earned = u?.referrals_months_earned || 0;
      let msg;
      if (referral && earned > 0) {
        msg = lang === 'fr' ? '1 mois Créateur offert !' : '1 free Creator month unlocked!';
      } else {
        msg = t('auth.created');
      }
      toast.success(msg);
      navigate('/onboarding');
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('auth.regfail'));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = () => {
    const redirect = encodeURIComponent(`${window.location.origin}/auth/google/callback`);
    window.location.href = `https://auth.emergentagent.com/?redirect=${redirect}`;
  };

  const refLabel = lang === 'fr' ? 'Code de parrainage (optionnel)' : 'Referral code (optional)';
  const refToggle = lang === 'fr' ? "J'ai un code de parrainage" : 'I have a referral code';

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-6 dot-grid py-10">
      <div className="w-full max-w-md p-8 rounded-2xl border border-white/10 bg-black/60 backdrop-blur-xl" data-testid="register-form">
        <div className="flex items-center gap-2 mb-6">
          <div className="w-9 h-9 rounded-md bg-[#D4FF00] flex items-center justify-center"><Sparkles className="w-4 h-4 text-black" strokeWidth={3} /></div>
          <span className="font-heading text-xl font-black">HighlightAI</span>
        </div>
        <h1 className="font-heading text-3xl font-black mb-1">{t('auth.create')}</h1>
        <p className="text-zinc-500 text-sm mb-6">{t('auth.create.sub')}</p>

        {referral && (
          <div className="mb-4 p-3 rounded-lg border border-[#D4FF00]/30 bg-[#D4FF00]/5 flex items-center gap-2 text-sm" data-testid="register-ref-banner">
            <Gift className="w-4 h-4 text-[#D4FF00]" />
            <span className="text-zinc-300">{lang === 'fr' ? 'Code appliqué' : 'Code applied'}: <span className="font-mono font-bold text-[#D4FF00]">{referral}</span> · {lang === 'fr' ? '+1 mois offert' : '+1 free month'}</span>
          </div>
        )}

        <Button onClick={handleGoogle} variant="outline" className="w-full border-white/15 bg-white/5 hover:bg-white/10 h-11 mb-4" data-testid="register-google-btn">
          <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24"><path fill="#fff" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/></svg>
          {t('auth.continue.google')}
        </Button>

        <div className="flex items-center gap-3 my-4">
          <div className="flex-1 h-px bg-white/10" />
          <span className="text-xs text-zinc-500">{t('auth.or')}</span>
          <div className="flex-1 h-px bg-white/10" />
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-zinc-400 text-xs uppercase tracking-wider mb-1.5 block">{t('auth.name')}</Label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <Input value={name} onChange={(e) => setName(e.target.value)} required placeholder="Alex Creator" className="pl-10 bg-white/5 border-white/10 h-11" data-testid="register-name-input" />
            </div>
          </div>
          <div>
            <Label className="text-zinc-400 text-xs uppercase tracking-wider mb-1.5 block">{t('auth.email')}</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <Input value={email} onChange={(e) => setEmail(e.target.value)} required type="email" placeholder="you@studio.com" className="pl-10 bg-white/5 border-white/10 h-11" data-testid="register-email-input" />
            </div>
          </div>
          <div>
            <Label className="text-zinc-400 text-xs uppercase tracking-wider mb-1.5 block">{t('auth.password')}</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <Input value={password} onChange={(e) => setPassword(e.target.value)} required type="password" placeholder="•••••• (6+ char)" minLength={6} className="pl-10 bg-white/5 border-white/10 h-11" data-testid="register-password-input" />
            </div>
          </div>

          {!showReferral ? (
            <button type="button" onClick={() => setShowReferral(true)} className="text-xs text-[#D4FF00] hover:underline inline-flex items-center gap-1.5" data-testid="register-ref-toggle"><Gift className="w-3.5 h-3.5" /> {refToggle}</button>
          ) : (
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider mb-1.5 block flex items-center gap-1"><Gift className="w-3 h-3" /> {refLabel}</Label>
              <div className="relative">
                <Input value={referral} onChange={(e) => setReferral(e.target.value.toUpperCase())} placeholder="ALEX-A3K2" className="bg-white/5 border-white/10 h-11 font-mono" data-testid="register-ref-input" />
              </div>
            </div>
          )}

          <Button type="submit" disabled={loading} className="w-full bg-[#D4FF00] text-black hover:bg-[#B3D600] font-semibold h-11" data-testid="register-submit-btn">
            {loading ? t('auth.creating') : t('auth.signup')}
          </Button>
        </form>

        <p className="text-center text-sm text-zinc-500 mt-6">
          {t('auth.haveaccount')} <Link to="/login" className="text-[#D4FF00] hover:underline" data-testid="register-link-login">{t('auth.signin.link')}</Link>
        </p>
      </div>
    </div>
  );
};

export default Register;
